import { fromJS, Map, List, Set } from 'immutable'
import { curry, propOr, isNil, flip } from 'ramda'
import { isFunction, isUndefined } from 'utils/types'

// deleteVal :: (String) -> Set -> Set
const deleteVal = curry((value, immSet) => immSet.delete(value))

// subtract :: (Array || Set || List) -> Set -> Set
const subtract = curry((collection, immSet) => immSet.subtract(collection))

// union :: (Array || Set || List) -> Set -> Set
const union = curry((collection, immSet) => immSet.union(collection))

// add :: (Number || String) -> Set -> Set
const add = curry((value, immSet) => immSet.add(value))

export function safeToJS(immObj, def = null) {
  if (immObj == null) {
    return def
  }

  if (immObj.toJS) return immObj.toJS()

  return immObj
}

// set :: (String, a) -> (Map || List) -> (Map || List)
const set = curry((propOrIndex, value, immObj) =>
  immObj.set(propOrIndex, fromJS(value))
)

// setIn :: ([String], a) -> (Map || List) -> (Map || List)
const setIn = curry((path, value, immObj) => immObj.setIn(path, fromJS(value)))

// setAllIn :: List -> List -> a -> (Map || List) -> (Map || List)
// if path is a function, use return for the path
const setAllIn = curry((objKeys, path, value, immObj) => {
  const reducer = isFunction(path)
    ? (acc, key) => acc.setIn(path(key), value)
    : (acc, key) => acc.setIn(path.concat(key), value)

  return objKeys.reduce(reducer, immObj)
})

// updateIn :: ([String], function) -> (Map || List) -> (Map || List)
const updateIn = curry((path, updater, immObj) =>
  immObj.updateIn(path, updater)
)

// update :: (String, function) -> (Map || List) -> (Map || List)
const update = curry((prop, updater, immObj) => immObj.update(prop, updater))

// if path is a function, use return for the path
const updateAllIn = curry((objKeys, path, updater, immObj) => {
  const reducer = isFunction(path)
    ? (acc, key) => acc.updateIn(path(key), updater)
    : (acc, key) => acc.updateIn(path.concat(key), updater)

  return objKeys.reduce(reducer, immObj)
})

// remove :: ([String]) -> (Map || List) -> (Map || List)
const remove = curry((key, immObj) => immObj.remove(key))

// removeIn :: ([String]) -> (Map || List) -> (Map || List)
const removeIn = curry((path, immObj) => immObj.removeIn(path))

// get :: String -> (Map || List) -> a
const get = curry((propOrIndex, immObj) => immObj && immObj.get(propOrIndex))

// get :: (Map || List) -> String -> a
const flipGet = flip(get)

// getIn :: [String] -> (Map || List) -> a
const getIn = curry((path, immObj) => immObj && immObj.getIn(path))

// has :: String -> (Map || List) -> Boolean
const has = curry((prop, immObj) => immObj && immObj.has && immObj.has(prop))

// hasIn :: [String] -> (Map || List) -> Boolean
const hasIn = curry((path, immObj) => immObj && immObj.hasIn(path))

// getInEq :: [String] -> a -> (Map || List) -> Boolean
const getInEq = curry(
  (path, value, immObj) => immObj && getIn(path, immObj) === value
)

const getInIncludes = curry(
  (path, value, immObj) => immObj && getIn(path, immObj).includes(value)
)

// getOr :: (a, String) -> (Map || List) -> (a || b)
// Note: if key value === undefined, this will return default
// don't store undefined as value, use null
const getOr = curry((defaultValue, propOrIndex, immObj) => {
  const property = immObj && immObj.get(propOrIndex)
  return isNil(property) ? defaultValue : property
})

// getInOr :: (a, [String]) -> (Map || List) -> (a || b)
// Note: if key value === undefined, this will return default
// don't store undefined as value, use null
const getInOr = curry((defaultValue, path, immObj) => {
  const property = immObj && immObj.getIn(path)
  return isNil(property) ? defaultValue : property
})

// push :: a -> List -> List
const push = curry((value, list) => list.push(value))

// concat :: List => List -> List
const concat = curry((value, list) => list.concat(value))

// unshift :: a -> List -> List
const unshift = curry((values, list) => list.unshift(values))

// filterNot :: Iter -> (Map || List) -> (Map || List)
const filterNot = curry((fn, immObj) => immObj.filterNot(fn))

// filter :: Iter -> (Map || List) -> (Map || List)
const filter = curry((fn, immObj) => immObj && immObj.filter(fn))

// map :: Iter -> (List) -> List
const map = curry((fn, immObj) => immObj && immObj.map(fn))

// map :: Iter -> (Map) -> List
const mapEntries = curry((fn, immObj) => immObj.mapEntries(fn))

// reduce :: Iter -> a -> (Map || List) -> b
const reduce = curry((fn, initialValue, immObj) =>
  immObj.reduce(fn, initialValue)
)

// some :: Iter -> (Map || List) -> b
const some = curry((fn, immObj) => immObj.some(fn))

// getEq :: (string || number) -> a -> -> (Map || List) -> Boolean
const getEq = curry(
  (prop, value, immObj) => immObj && immObj.get(prop) === value
)

// isImmutable :: a -> boolean
const isImmutable = propOr(false, 'get')

// pickToJS :: [String] -> (Map || List) -> Object
const pickToJS = curry((fieldNames, immObj) => {
  if (!immObj || !immObj.get) {
    return null
  }
  return fieldNames.reduce((acum, name) => {
    const item = immObj.get(name)
    return {
      ...acum,
      [name]: isImmutable(item) ? item.toJS() : item,
    }
  }, {})
})

// pick :: Map -> List -> Map
const pick = curry(
  (objKeys, object) =>
    object &&
    objKeys &&
    objKeys.reduce((acc, key) => acc.set(key, object.get(key)), Map())
)

const pickIn = curry(
  (objKeys, path, obj) =>
    obj &&
    objKeys &&
    path &&
    objKeys.reduce((acc, key) => acc.set(key, obj.getIn([...path, key])), Map())
)

// omit :: Map -> List -> Map
const omit = curry(
  (objKeys, object) =>
    object && objKeys && filterNot((_, key) => includes(objKeys, key), object)
)

const includes = curry((list, key) => list && key && list.includes(key))

// change to pick to List
const pickToArray = curry((keys, object) => {
  if (!object || !keys) return List()

  return keys.reduce((acc, key) => {
    const item = object.get(key)

    if (!isUndefined(item)) return acc.update(push(item))

    return acc
  }, List())
})

const keys = (collection) => {
  if (!collection) return null

  return collection.keySeq()
}

const values = (collection) => {
  if (!collection) return null

  return collection.valueSeq()
}

// if path is a function, use return for the path
const removeAllIn = curry((objKeys, path, immObj) => {
  const reducer = isFunction(path)
    ? (acc, key) => acc.removeIn(path(key))
    : (acc, key) => acc.removeIn(path.concat(key))

  return objKeys.reduce(reducer, immObj)
})

/**
 * Finds all keys that match predicate
 *
 * @param {array} objKeys
 * @param {string} targetKey
 * @param {Map} immObj
 *
 * @return {Map}
 */
const removeAll = curry((objKeys, targetKey, immObj) => {
  const reducer = (acc, key) => acc.removeIn([targetKey].concat(key))

  return objKeys.reduce(reducer, immObj)
})

// mergeDeep :: Obj -> Map -> Map
const mergeDeep = curry((src, target) => target.mergeDeep(src))

const merge = curry((src, target) => target.merge(src))
const mergeWith = curry((updater, target, src) =>
  target.mergeWith(updater, src)
)

const mergeIn = curry((path, src, target) => target.mergeIn(path, src))

// Map -> List
const valueSeq = (immObj) => immObj.valueSeq()

const where = curry((obj, immObj) => {
  const props = Object.keys(obj)
  return props.reduce((acc, value) => {
    if (acc === false) return false

    return obj[value](get(value, immObj))
  }, true)
})

/**
 * @param {{ [key]: comparitorFunction, ... }} spec
 * @param {Map} immObjOne
 * @param {Map} immObjTwo
 *
 * accesses the values of each immObj by key in the spec and passes
 * the values to the comparitor function
 *
 * @return {boolean}
 */
const matchAnyCommonKey = curry((spec, immObjOne, immObjTwo) =>
  Object.entries(spec).some(([key, callback]) =>
    callback(get(key, immObjOne), get(key, immObjTwo))
  )
)

const getSatisfies = curry((fn, prop, immObj) => fn(get(prop, immObj)))

const getInSatisfies = curry((fn, path, immObj) => fn(getIn(path, immObj)))

const pluck = curry((prop, immObj) => immObj.map(get(prop)))

const first = (immObj) => immObj.first()

const find = curry((predicate, immObj) => {
  if (!isImmutable(immObj)) return null

  return immObj.find(predicate)
})

const slice = curry((start = 0, end = null, immObj) => {
  if (!isImmutable(immObj)) return List()

  return immObj.slice(start, end)
})
const sliceToJS = curry((start = 0, end = null, immObj) => {
  if (!isImmutable(immObj)) return List()

  return immObj.slice(start, end).toJS()
})

const findKey = curry((predicate, immObj) => {
  if (!isImmutable(immObj)) return null

  return immObj.findKey(predicate)
})

/**
 * Finds all keys that match predicate
 *
 * @param {fn} predicate
 * @param {Map} immObj
 *
 * @example
 *    const map = fromJS({
 *      found: { x: 1 },
 *      notFound: { x: 3 },
 *      alsoNotFound: { y: 1 },
 *      alsoFound: { x: 1 },
 *    })
 *
 *    findKeysBy(getEq('x', 1), map)
 *    // => Set(['found', 'alsoFound'])
 *
 */
const findKeysBy = curry((predicate, immObj) => {
  if (!immObj || !isImmutable(immObj)) {
    throw new Error(
      'You are attempting to use `findKeysBy` on a non-immutable object'
    )
  }

  if (!Map.isMap(immObj) || !isFunction(predicate)) {
    throw new Error('Invalid parameters passed to `findKeysBy`')
  }

  return immObj.reduce((acc, value, key) => {
    if (predicate(value)) return acc.add(key)

    return acc
  }, Set())
})

const sortBy = curry((predicate, immObj) => {
  if (!isImmutable(immObj)) return null

  return immObj.sortBy(predicate)
})

/**
 * Gets the difference of two lists
 *
 * @param {fn} cmp
 * @param {List} list1
 * @param {List} list2
 *
 * @example
 *    const list1 = List([{a: 1}, {a: 2}, {a: 3}])
 *    const list2 = List([{a: 3}, {a: 4}])
 *
 *    const cmp = (x, y) => x.get('a') === y.get('a')
 *
 *    differenceWith(cmp, list1, list2) // => [{a: 1}, {a: 2}]
 */
const differenceWith = curry((cmp, list1, list2) => {
  if (list1.isEmpty() && list2.isEmpty()) return List()
  if (list2.isEmpty()) return list1

  return list1.reduce((acc, item) => {
    if (item.isEmpty()) return acc

    if (list2.some((list2Item) => cmp(item, list2Item))) {
      return acc
    }

    return acc.update(push(item))
  }, List())
})

const differenceWithArray = curry((cmp, list1, list2) => {
  if (list1.isEmpty() && list2.isEmpty()) return List()
  if (list2.isEmpty()) return list1

  return list1.reduce((acc, item) => {
    if (!item) return acc

    if (list2.some((list2Item) => cmp(item, list2Item))) {
      return acc
    }

    return acc.update(push(item))
  }, List())
})

/**
 * recursively flattens a list
 * immutable.js `flatten` will flatten any collection
 *
 * @param {List} list
 *
 * @example
 *    const list = fromJS([1, 2, [3, 4, [5, 6], [7, 8]], 9, 10])
 *    flattenList(list)
 *    // => List([1, 2, 3, 4, 5, 6, 7, 8, 9, 10])
 */
const flattenList = (list) => {
  if (!list || !isImmutable(list)) {
    throw new Error(
      'You are attempting to use `flattenList` on a non-immutable object'
    )
  }

  if (!List.isList(list)) {
    throw new Error(
      "`flattenList` must be called with an immutable List as it's only argument"
    )
  }

  if (list.isEmpty()) return list

  return list.reduce((acc, current) => {
    const value = List.isList(current) ? flattenList(current) : current

    // immutable.js will also concat Maps/Sets etc,
    // so push when a list is not found
    return List.isList(value) ? acc.concat(value) : acc.push(value)
  }, List())
}

// equals :: immObj => immObj => Boolean
const equals = curry((a, b) => a.equals(b))

// isEmpty :: immObj => immObj => Boolean
const isEmpty = (immObj) => immObj.isEmpty()
const notEmpty = (immObj) => !immObj.isEmpty()

/**
 * @param {obj} transformations
 * @param {*} arg
 * @param {Map || List} immObj
 *
 * @returns updated version of third argument
 */
const evolveWith = curry((transformations, arg, immObj) =>
  Object.entries(transformations).reduce(
    (accum, [key, updater]) => update(key, updater(arg, immObj), accum),
    immObj
  )
)

export {
  set,
  setIn,
  setAllIn,
  updateIn,
  updateAllIn,
  update,
  remove,
  removeIn,
  get,
  getIn,
  has,
  hasIn,
  getInEq,
  getOr,
  getInOr,
  push,
  concat,
  unshift,
  filterNot,
  filter,
  getEq,
  pickToJS,
  pick,
  pickToArray,
  keys,
  values,
  map,
  mapEntries,
  reduce,
  removeAllIn,
  mergeDeep,
  merge,
  mergeWith,
  mergeIn,
  slice,
  sliceToJS,
  some,
  valueSeq,
  deleteVal,
  subtract,
  add,
  union,
  where,
  getSatisfies,
  getInSatisfies,
  pluck,
  differenceWith,
  differenceWithArray,
  first,
  evolveWith,
  flattenList,
  find,
  findKey,
  findKeysBy,
  sortBy,
  equals,
  isEmpty,
  removeAll,
  omit,
  includes,
  flipGet,
  notEmpty,
  getInIncludes,
  pickIn,
  matchAnyCommonKey,
}
